import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import RotationHeader from './components/RotationHeader';
import StatsBar from './components/StatsBar';
import FlightTimeline from './components/FlightTimeline';
import CompliancePanel from './components/CompliancePanel';
import { getCompleteRotation } from '../../services/apiService';
import { supabase } from '../../lib/supabase';

/**
 * 🚀 ROTATION DETAIL V3 - THE BADASS EDITION
 * 
 * Philosophy:
 * - MOBILE FIRST - Most users on phones/tablets
 * - SINGLE SOURCE - compiled_json ONLY (no parsed data!)
 * - THREE-ID SYSTEM - rotation_id, parsed_rotation_id, compiled_rotation_id, employee_id, employee_name
 * - FAST AF - <1 second load, cached, optimized
 * - SEXY AF - Modern design, smooth animations, intuitive
 * - COMPLETE - Hotels, layovers, pay, compliance - everything visible
 * 
 * Data Flow:
 * 1. Single API call → /complete endpoint
 * 2. Backend reads compiled_rotations table ONLY
 * 3. Frontend gets complete data package
 * 4. Render fast, cache smart, look amazing
 */

const RotationDetailV3 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rotationId = searchParams.get('id');
  
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hourlyRate, setHourlyRate] = useState(null);
  const [isLoadingRate, setIsLoadingRate] = useState(true);

  const breadcrumbItems = [
    { label: 'Home', path: '/dashboard', icon: 'Home' },
    { label: 'Data', path: '/data-management', icon: 'Database' },
    { label: `#${data?.summary?.rotation_number || rotationId}`, icon: 'Plane' }
  ];

  useEffect(() => {
    if (!rotationId) {
      setError('No rotation ID provided');
      setIsLoading(false);
      return;
    }
    
    loadData();
  }, [rotationId, refreshKey]);

  // Fetch hourly rate from Supabase (same pattern as timecard page)
  useEffect(() => {
    const fetchHourlyRate = async () => {
      if (!data || !data.summary) {
        setIsLoadingRate(false);
        return;
      }

      const employeeId = data.summary.employee_id;
      const rotationStartDate = data.summary.dates?.start;

      console.log('🔍 Rotation: Checking for employee_id:', employeeId);
      
      if (!employeeId) {
        console.warn('⚠️ No employee_id found in rotation summary');
        setIsLoadingRate(false);
        return;
      }

      try {
        // Step 1: Get UUID from employee_id via user_profiles
        const employeeIdVariants = [
          employeeId,                          // As-is (e.g., "0141986")
          employeeId.replace(/^0+/, ''),      // Strip leading zeros (e.g., "141986")
        ];
        
        console.log('🔍 Trying employee ID variants:', employeeIdVariants);
        
        let profile = null;
        for (const variant of employeeIdVariants) {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('employee_id', variant)
            .maybeSingle();
          
          if (data) {
            profile = data;
            console.log(`✅ Found profile with employee_id: ${variant}`);
            break;
          }
        }

        if (!profile) {
          console.warn('⚠️ No user profile found for any employee_id variant:', employeeIdVariants);
          setIsLoadingRate(false);
          return;
        }

        console.log('✅ Found UUID:', profile.id);

        // Step 2: Get user_settings with that UUID
        const { data: settings, error: settingsError } = await supabase
          .from('user_settings')
          .select('*')
          .eq('user_id', profile.id)
          .maybeSingle();

        console.log('Settings query result:', { settings, settingsError });

        if (settingsError) {
          console.error('❌ Settings query error:', settingsError);
          setIsLoadingRate(false);
          return;
        }

        if (!settings) {
          console.warn('⚠️ No user settings found for user_id:', profile.id);
          setIsLoadingRate(false);
          return;
        }

        console.log('✅ Got settings:', settings);

        // Step 3: Calculate pay rate from position + hire date + aircraft + ROTATION YEAR
        if (settings.position && settings.date_of_hire && settings.default_aircraft_model) {
          // Get the rotation year from start date
          let rotationYear = new Date().getFullYear(); // Default to current year
          if (rotationStartDate) {
            rotationYear = new Date(rotationStartDate).getFullYear();
          }
          
          console.log(`📅 Rotation year: ${rotationYear}`);
          
          // Calculate years of service AS OF THE ROTATION DATE
          const hire = new Date(settings.date_of_hire);
          const rotationDate = rotationStartDate ? new Date(rotationStartDate) : new Date();
          const yearsOfService = Math.floor((rotationDate - hire) / (365.25 * 24 * 60 * 60 * 1000)) + 1;
          
          // Map position to pay_scales format
          const positionMap = { 
            'CA': 'Captain', 
            'Captain': 'Captain', 
            'A': 'Captain',
            'FO': 'First Officer', 
            'First Officer': 'First Officer',
            'B': 'First Officer'
          };
          const payPosition = positionMap[settings.position] || settings.position;
          
          // Map aircraft codes to full names in pay_scales
          const aircraftMap = {
            '73N': 'B-737-900',
            '737-900': 'B-737-900',
            'B737-900': 'B-737-900',
            '76I': 'B-767-400',
            '767-400': 'B-767-400',
            'B767-400': 'B-767-400',
            '777': 'B-777',
            'B777': 'B-777',
            '787': 'B-787',
            'B787': 'B-787'
          };
          const mappedAircraft = aircraftMap[settings.default_aircraft_model] || settings.default_aircraft_model;
          
          console.log('🔍 Looking up pay rate:', { payPosition, mappedAircraft, yearsOfService, rotationYear });
          
          const { data: payScales, error: payError } = await supabase
            .from('pay_scales')
            .select('*')
            .eq('position', payPosition)
            .eq('aircraft_model', mappedAircraft)
            .eq('pay_year', Math.min(yearsOfService, 12))  // Cap at year 12
            .lte('effective_date', `${rotationYear}-12-31`)  // Rate must be effective by end of rotation year
            .order('effective_date', { ascending: false })
            .limit(1);

          console.log('💰 Pay scales query result:', payScales, payError);

          if (payScales && payScales.length > 0) {
            const rate = parseFloat(payScales[0].hourly_rate);
            console.log(`✅ Using ${rotationYear} rate: $${rate}/hr (effective ${payScales[0].effective_date})`);
            setHourlyRate(rate);
          } else {
            console.warn('⚠️ No pay rate found for:', { payPosition, mappedAircraft, yearsOfService, rotationYear });
          }
        }
      } catch (error) {
        console.error('❌ Failed to load hourly rate:', error);
      } finally {
        setIsLoadingRate(false);
      }
    };

    fetchHourlyRate();
  }, [data]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🚀 Loading V3 complete rotation data:', rotationId);

      // Check cache first (5 minute TTL)
      const CACHE_TTL = 5 * 60 * 1000;
      const cacheKey = `rotation_v3_${rotationId}`;
      const cached = sessionStorage.getItem(cacheKey);
      
      if (cached) {
        try {
          const { data: cachedData, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          
          if (age < CACHE_TTL) {
            console.log(`✅ Cache hit (${Math.round(age / 1000)}s old)`);
            setData(cachedData);
            setIsLoading(false);
            return;
          } else {
            console.log('⏱️ Cache expired');
            sessionStorage.removeItem(cacheKey);
          }
        } catch (cacheErr) {
          console.warn('⚠️ Cache error:', cacheErr);
          sessionStorage.removeItem(cacheKey);
        }
      }

      // 🚀 SINGLE API CALL - compiled_json ONLY!
      const completeData = await getCompleteRotation(rotationId);
      console.log('✅ V3 data loaded:', completeData);
      
      setData(completeData);

      // Cache for next time
      sessionStorage.setItem(cacheKey, JSON.stringify({
        data: completeData,
        timestamp: Date.now()
      }));
      console.log('💾 Cached for 5 minutes');

    } catch (err) {
      console.error('❌ Load failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load rotation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    const cacheKey = `rotation_v3_${rotationId}`;
    sessionStorage.removeItem(cacheKey);
    setRefreshKey(prev => prev + 1);
  };

  // Loading state with skeleton
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="lg:ml-60 min-h-screen">
          <div className="container-custom py-4 md:py-6 space-y-4">
            {/* Skeleton Header */}
            <div className="h-32 bg-card rounded-xl animate-pulse" />
            {/* Skeleton Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 bg-card rounded-lg animate-pulse" />
              ))}
            </div>
            {/* Skeleton Content */}
            <div className="h-96 bg-card rounded-xl animate-pulse" />
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="lg:ml-60 min-h-screen">
          <div className="container-custom py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 rounded-full bg-error/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" className="w-10 h-10 text-error" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Failed to Load</h3>
                <p className="text-muted-foreground mb-6">{error}</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => navigate('/data-management')}
                    className="btn btn-outline"
                  >
                    <Icon name="ArrowLeft" className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={handleRefresh}
                    className="btn btn-primary"
                  >
                    <Icon name="RefreshCw" className="w-4 h-4" />
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Not found state
  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="lg:ml-60 min-h-screen">
          <div className="container-custom py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="FileQuestion" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">Rotation Not Found</h3>
                <button
                  onClick={() => navigate('/data-management')}
                  className="btn btn-primary mt-4"
                >
                  Back to Data Management
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // 🚀 MAIN CONTENT - BADASS MOBILE-FIRST DESIGN
  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="lg:ml-60 min-h-screen">
        {/* Mobile: Compact padding, Desktop: More breathing room */}
        <div className="container-custom py-3 md:py-6 space-y-3 md:space-y-4">
          
          {/* Breadcrumb - Hidden on mobile to save space */}
          <div className="hidden md:block">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Header - Compact employee info, rotation number, dates */}
          <RotationHeader 
            rotation={data.rotation}
            summary={data.summary}
            compliance={data.compliance}
            onBack={() => navigate('/data-management')}
            onRefresh={handleRefresh}
          />

          {/* Quick Stats - 2 cols mobile, 4 cols desktop */}
          <StatsBar 
            stats={data.summary.totals}
            pay={data.pay}
            hourlyRate={hourlyRate}
            isLoadingRate={isLoadingRate}
          />

          {/* Main Content - Single column mobile, 2 column desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
            
            {/* LEFT: Flight Timeline + Pay (70% desktop) */}
            <div className="lg:col-span-2 space-y-4">
              <FlightTimeline 
                flights={data.flights}
                layovers={data.layovers}
                summary={data.summary}
              />
            </div>

            {/* RIGHT: Compliance Sidebar (30% desktop, below on mobile) */}
            <div className="lg:col-span-1">
              <CompliancePanel 
                compliance={data.compliance}
                pay={data.pay}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RotationDetailV3;
